import React from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "../../../../../components/Dropdown";
import Checkbox from "../../../../../components/Checkbox";
import Badge from "../../../../../components/Badge";
import TextArea from "../../../../../components/TextArea";
import { Validation } from "../../../../../interfaces/common";
import { NoteForm } from "../../../../../interfaces/payout.interface";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";

const NOTE_TAGS: { label: string; variant: 'primary' | 'success' | 'info' | 'warning' | 'danger' | 'secondary' }[] = [
  { label: "Tax mismatch", variant: "primary" },
  { label: "Guest deposit", variant: "success" },
  { label: "Extra cleaning", variant: "info" },
  { label: "Extra check time fees", variant: "warning" },
  { label: "Damage payment", variant: "danger" },
  { label: "Damage claim", variant: "secondary" },
];

interface NoteModalContentProps {
  item: any | null;
  formData: NoteForm;
  validation: Validation;
  actions: any[];
  allActions: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
  onInputBlur: (e: any, name: string) => void;
}

const NoteModalContent: React.FC<NoteModalContentProps> = ({
  item,
  formData,
  validation,
  actions,
  allActions,
  dropdownSearchData,
  onDropdownSearchChange,
  onInputChange,
  onInputBlur,
}) => {
  const { t } = useTranslation();

  const getValidationMessage = (field: string) => {
    return validation.fields.find((item) => item.field === field)?.message;
  };

  const actionError = getValidationMessage(VALIDATION_FIELD_MAPPING.action);
  const notesError = getValidationMessage(VALIDATION_FIELD_MAPPING.notes);

  return (
    <div className="py-2">
      {item && (
        <div className="mb-4">
          <p className="fw-bold mb-1 text-dark">{item.property_name}</p>
          <p className="text-muted mb-0">
            {item.guest_name} ({item.check_in_date} - {item.check_out_date})
          </p>
        </div>
      )}

      <div className="row g-3">
        <div className="col-12">
          <Dropdown
            values={formData.action ? [formData.action] : []}
            options={(actions || []).map((action: any) => ({
              label: action.name,
              value: action.id === '' ? '__blank__' : action.id,
            }))}
            allOptions={(allActions || []).map((action: any) => ({
              label: action.name,
              value: action.id === '' ? '__blank__' : action.id,
            }))}
            id={VALIDATION_FIELD_MAPPING.action}
            name={VALIDATION_FIELD_MAPPING.action}
            label={t("reconciliation.payout.list.pageTexts.modals.note.fields.action.label")}
            placeholder={t("reconciliation.payout.list.pageTexts.modals.note.fields.action.placeholder")}
            searchText={dropdownSearchData.action}
            onSearch={onDropdownSearchChange}
            onChange={onInputChange}
            onBlur={onInputBlur}
            error={!!actionError}
            errorMessage={actionError}
            required
          />
        </div>

        <div className="col-md-12">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.notes}
              name={VALIDATION_FIELD_MAPPING.notes}
              label={t("reconciliation.payout.list.pageTexts.modals.note.fields.notes.label")}
              placeholder={t("reconciliation.payout.list.pageTexts.modals.note.fields.notes.placeholder")}
              rows={4}
              value={formData.notes || ""}
              error={!!notesError}
              errorMessage={notesError}
              onChange={onInputChange}
              onBlur={onInputBlur}
              className="form-control-lg h-100"
            />
          </div>
        </div>

        <div className="col-12">
          <div className="mt-1">
            <p className="text-muted small fw-semibold mb-2 text-uppercase ls-1">
              {t("reconciliation.payout.list.pageTexts.modals.note.fields.tags")}
            </p>
            <div className="d-flex flex-wrap gap-2">
              {NOTE_TAGS.map((tag) => (
                <Badge
                  key={tag.label}
                  text={tag.label}
                  variant={tag.variant}
                  isTag
                  onClick={() => {
                    const current = formData.notes || "";
                    const updated = current ? `${current}\n${tag.label}` : tag.label;
                    onInputChange({ target: { value: updated } } as any, VALIDATION_FIELD_MAPPING.notes);
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {!item && (
          <div className="col-12 form-floating form-floating-outline mt-3">
            <Checkbox
              variant="primary"
              className="d-checkbox mb-0"
              checked={!!formData.override}
              label={t("reconciliation.payout.list.pageTexts.modals.note.fields.override.label")}
              name="override"
              onChange={onInputChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NoteModalContent;
